// apps/nos/client/src/hooks/useInstruments.ts
//
// Shared React Query keys + catalog hooks for the Manage Chromatography
// feature. Used by InstrumentsPage and the Substance Library drawer
// chromatograms section.
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCatalogs,
  getDefault,
  listConfigs,
  type ConfigDTO,
  type TypeColumnRow,
  type TypeInstrumentRow,
} from "@/services/instruments_api";
import type {
  AnalyserId,
  AnalyserType,
  GcColumnFamily,
  InstrumentCatalogEntry,
  InstrumentPlatform,
  InstrumentVendor,
  InstrumentVendorId,
} from "@/utils/types/instrument";

// ─── React Query key namespace ────────────────────────────────────────
//
// One place for every query key under the instruments feature so
// invalidation is consistent across files. Centralising the key shape
// also lets us refactor key contents without hunting through pages.
export const instrumentsKeys = {
  catalogs: ["instruments", "catalogs"] as const,
  configs: ["instruments", "configs"] as const,
  config: (id: string) => ["instruments", "config", id] as const,
  configLadders: (id: string) =>
    ["instruments", "config", id, "ladders"] as const,
  configChromas: (id: string) =>
    ["instruments", "config", id, "chromatograms"] as const,
  default: ["instruments", "default"] as const,
  tags: ["instruments", "tags"] as const,
  orphans: ["instruments", "orphans"] as const,
  invChromas: (invId: string) =>
    ["inventory", invId, "chromatograms"] as const,
};

// ─── Static lookup tables ─────────────────────────────────────────────
//
// These two arrays carry human-readable labels for the analyser types
// and vendors that the DB stores as raw strings. They're not derived
// from the catalogs endpoint — they're presentation metadata.

export const ANALYSER_TYPES: AnalyserType[] = [
  { id: "single_quadrupole", label: "Single quadrupole", short: "Single Q", resolution: "Unit-mass" },
  { id: "triple_quadrupole", label: "Triple quadrupole", short: "Triple Q", resolution: "Unit-mass" },
  { id: "q_tof", label: "Quadrupole–TOF", short: "Q-TOF", resolution: "High-resolution" },
  { id: "orbitrap", label: "Orbitrap", short: "Orbitrap", resolution: "High-resolution" },
  { id: "time_of_flight", label: "Time of Flight", short: "TOF", resolution: "High-resolution" },
];

export const INSTRUMENT_VENDORS: InstrumentVendor[] = [
  { id: "Agilent", label: "Agilent" },
  { id: "Thermofisher", label: "Thermo Fisher" },
];

export function findAnalyser(
  id: string | null | undefined,
): AnalyserType | null {
  return ANALYSER_TYPES.find((x) => x.id === id) ?? null;
}

export function instrumentVendorAccent(
  vendor: string | null | undefined,
): string {
  if (vendor === "Agilent") return "var(--fam-citrus)";
  if (vendor === "Thermofisher") return "var(--fam-fruity)";
  return "var(--ink-3)";
}

// ─── Adapters: server rows → UI catalog shape ─────────────────────────

// Platform is derived from the instrument_name string. "GC-MS" / "LC-MS"
// is always present in the model name in the canonical nfood.type_instrument
// catalog (e.g., "5977B/C GC-MSD", "6120 LC/MSD").
function derivePlatform(instrumentName: string): InstrumentPlatform {
  const upper = instrumentName.toUpperCase();
  if (upper.includes("LC")) return "LC-MS";
  return "GC-MS";
}

function rowToInstrumentEntry(
  row: TypeInstrumentRow,
): InstrumentCatalogEntry | null {
  const vendor = row.instrument_provider as InstrumentVendorId | null;
  if (vendor !== "Agilent" && vendor !== "Thermofisher") return null;
  const analyser = row.analyser_type as AnalyserId | null;
  if (!analyser) return null;
  return {
    id: row.id,
    label: row.instrument_name,
    family: row.instrument_family,
    vendor,
    analyser,
    platform: derivePlatform(row.instrument_name),
  };
}

function rowToColumnFamily(row: TypeColumnRow): GcColumnFamily {
  // Stationary phase composition is a jsonb shape; flatten to a single
  // readable line for the picker hint. Fall back to the agilent_equivalent
  // when the jsonb shape isn't a simple description string.
  let phase = row.agilent_equivalent || "";
  if (row.stationary_phase_composition && typeof row.stationary_phase_composition === "object") {
    const entries = Object.entries(row.stationary_phase_composition as Record<string, unknown>);
    if (entries.length) {
      phase =
        entries
          .map(([k, v]) => `${v}% ${k}`)
          .join(" / ") || phase;
    }
  }
  return {
    id: row.id,
    family: row.column_product_name,
    polarity: row.polarity as GcColumnFamily["polarity"],
    phase,
    typical: "",
  };
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useInstrumentsCatalogs() {
  const catalogsQ = useQuery({
    queryKey: instrumentsKeys.catalogs,
    queryFn: fetchCatalogs,
    staleTime: 60_000,
  });

  const instrumentCatalog = useMemo<InstrumentCatalogEntry[]>(() => {
    const rows = catalogsQ.data?.instruments;
    if (!Array.isArray(rows)) return [];
    return rows
      .map(rowToInstrumentEntry)
      .filter((e): e is InstrumentCatalogEntry => e !== null);
  }, [catalogsQ.data]);

  const columnFamilies = useMemo<GcColumnFamily[]>(() => {
    const rows = catalogsQ.data?.columns;
    if (!Array.isArray(rows)) return [];
    return rows.map(rowToColumnFamily);
  }, [catalogsQ.data]);

  const findInstrumentCatalog = useCallback(
    (id: number | null | undefined): InstrumentCatalogEntry | null =>
      id == null ? null : instrumentCatalog.find((x) => x.id === id) ?? null,
    [instrumentCatalog],
  );

  const findColumnFamily = useCallback(
    (id: number | null | undefined): GcColumnFamily | null =>
      id == null ? null : columnFamilies.find((x) => x.id === id) ?? null,
    [columnFamilies],
  );

  return {
    catalogsQ,
    instrumentCatalog,
    columnFamilies,
    findInstrumentCatalog,
    findColumnFamily,
  };
}

export function useDefaultUserExpConfig() {
  return useQuery({
    queryKey: instrumentsKeys.default,
    queryFn: getDefault,
    staleTime: 60_000,
  });
}

// Lists the caller's saved experiment configurations (org-scoped + personal).
// Only configs that round-tripped through R&D registration come back (the
// server filters out current_nfood_experiment_information_id IS NULL), so every
// id here is a valid user_exp_config_id usable as a per-file config assignment.
export function useUserExpConfigs() {
  return useQuery<ConfigDTO[]>({
    queryKey: instrumentsKeys.configs,
    queryFn: listConfigs,
    staleTime: 60_000,
  });
}
